import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import Illutration from "../../assets/Illustration/qr-illustration.png";
import Config from "../../config";
import { useCall } from "../../call-sdk/context/CallContext";
import { Typography } from "@mui/material";
import Button from "../../common/Button";
import { useNavigate } from "react-router-dom";
import {
  Container,
  LogoContainer,
  ButtonContainer,
  IllustrationContainer,
  ContentArea,
} from "./LandingPageStyles";
import ErrorBoundary from "../ErrorBoundary";
import LocalStorage from "src/call-sdk/lib/local-storage";
import EventTracker from "src/pendo/EventTracker";
import StorageConstants from "src/config/storage.constants";
import { QrAuthRequest } from "../../types";
import { authLogin } from "src/services/qr-auth.service";
import { getBusinessListById } from "src/services/qr-business-details.service";
import HU_Illutration from "../../assets/Illustration/hu-qr-illustration.png";
import { DUContent, HUContent } from "./Content";
import { getDeviceId } from "src/lib/generateDeviceId";
import { unlockAudioPlayback } from "src/util/audio-playback.util";

declare global {
  interface Window {
    pendoVisitorId: string;
    pendo: any;
  }
}

function LandingPage() {
  const { business, isCallRejected, callRejectedStatus, error, clearError } =
    useCall();
  const navigate = useNavigate();
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [uuid, setUuid] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);

  const regionConfig = Config[`${process.env.REACT_APP_REGION}`];

  const isHUUser = Boolean(LocalStorage.get(StorageConstants.IS_HU_USER));

  useEffect(() => {
    const fetchAuthToken = async () => {
      const deviceid = getDeviceId();
      let authRequestBody: QrAuthRequest = {
        qrCodeId: LocalStorage.get(StorageConstants.QR_ID),
        deviceid,
      };
      const { data, error } = await authLogin(authRequestBody);
      if (data) {
        const businessId = data.user.businessId;
        setUuid(businessId);

        const businessResult = await getBusinessListById(businessId);
        if (businessResult?.data) {
          const business = businessResult.data.businessList[0];
          setBusinessName(business.businessName);
          setPhoneNumber(business.phoneNumber);

          EventTracker.handleUserIdentification(
            businessId,
            business.phoneNumber,
            { businessName: business.businessName },
            {
              businessPhoneNumber: business.phoneNumber,
              businessType: business.businessType,
            },
          );
        } else if (businessResult.error) {
          console.error(
            "Error fetching business details:",
            businessResult.error,
          );
        }
      } else if (error) {
        console.error("Error fetching auth token:", error);
      }
    };
    if (!isHUUser) {
      fetchAuthToken();
    }
  }, [isHUUser]);

  useEffect(() => {
    if (uuid || businessName || phoneNumber) {
      // No-op function to use the variables
    }
  }, [uuid, businessName, phoneNumber]);

  const openPermissionPage = () => {
    // Runs inside the Start/Join tap so remote call audio can play later (iOS 27).
    unlockAudioPlayback();
    LocalStorage.remove("hasReloaded");
    if (isCallRejected) {
      callRejectedStatus();
    }
    navigate({ pathname: "/qr-permission" }, { replace: true });
  };
  useEffect(() => {
    changeAddressBarColor();
  }, []);
  const changeAddressBarColor = () => {
    let metas = document.head.getElementsByTagName("meta");
    metas[2].content = "#ffffff";
    document.body.style.backgroundColor = "#ffffff";
  };

  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [clearError, error]);

  return (
    <ErrorBoundary>
      <Container>
        <LogoContainer justifyContent={isHUUser ? "center" : "space-between"}>
          <LazyLoadImage
            src={regionConfig.appLogoImageSrc}
            alt="Convo Logo"
            effect="blur"
          />
          {!isHUUser && (
            <LazyLoadImage
              src={business?.photoURL ?? ""}
              alt="Business Logo"
              effect="blur"
            />
          )}
        </LogoContainer>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            columnGap: "20px",
            gap: "12px",
          }}
        >
          <IllustrationContainer>
            <LazyLoadImage
              src={!!isHUUser ? HU_Illutration : Illutration}
              alt="Convo Illustration"
              effect="blur"
              style={{
                display: "block",
                objectFit: "contain",
                width: "100%",
                maxHeight: "24.24vh",
              }}
            />
          </IllustrationContainer>

          <ContentArea>
            {!!isHUUser ? <HUContent /> : <DUContent />}
          </ContentArea>
        </div>

        <ButtonContainer>
          <Typography variant="subtitle2" gutterBottom className="terms">
            By starting, you agree to&nbsp;
            {regionConfig.companyName}'s &nbsp;
            <a
              className="link"
              target="_blank"
              rel="noopener noreferrer"
              href={regionConfig.legalURL}
              style={{ color: `rgba(24, 132, 235, 1)` }}
              aria-label="Terms and Conditions"
            >
              Terms and Conditions
            </a>
            .
          </Typography>
          <Button
            className="make-call-btn"
            size="large"
            fullWidth
            onClick={openPermissionPage}
            style={{
              backgroundColor: "#00AA55",
              border: "none",
              borderRadius: "100px",
            }}
            aria-label="Start"
          >
            {!!isHUUser ? "Join" : "Start"}
          </Button>
        </ButtonContainer>
      </Container>
    </ErrorBoundary>
  );
}

export default LandingPage;
